// « Calculateur de profit » — the pure combination logic (owner, 2026-10-22):
// « when I count the revenue I need a filter, clean and easy: revenue minus the
// printer cost; if I add the ad costs it's a different calculation; if I add the
// AI costs, different again — all the criteria in the same place, I switch them
// on and off, so I know everything. »
//
// The worker returns every cost line separately (GET /dashboard/profit →
// profit.components, the ONE formula of worker lib/profit.ts). The page only
// recombines: Encaissé − each line that is switched on = Résultat. All lines on
// = the official « Profit net ». A line that cannot be computed (« données
// manquantes ») makes the result unknown while it is ON — never a fake 0 — and
// switching it off gives the known part back.
//
// Coût IA Réel / Payé (« Fournisseurs IA », 2026-10-23): a small switch on the
// « Coût IA » chip picks which AI figure is subtracted — RÉEL (default, the full
// token value, even what Claude's free credit covers) or PAYÉ (the cash:
// OpenRouter + Claude not covered). The worker sends both (profit.ai.total /
// profit.ai.paid); withAiBasis() swaps the AI line, nothing else changes.
import type { AiBasis, ProfitComponentKey, ProfitComponents, ProfitSummary } from './dashboardApi';

/** The chips, in the order they are subtracted (and drawn). */
export const COST_LINES: ReadonlyArray<{ key: ProfitComponentKey; label: string }> = [
  { key: 'production', label: 'Impression (fabrication)' },
  { key: 'delivery', label: 'Livraison' },
  { key: 'commissions', label: 'Commissions' },
  { key: 'ads', label: 'Pub' },
  { key: 'ai', label: 'Coût IA' },
  { key: 'expenses', label: 'Charges' },
];

export type Toggles = Record<ProfitComponentKey, boolean>;

/** Default = every line on = the official Profit net. */
export const ALL_ON: Toggles = { production: true, delivery: true, commissions: true, ads: true, ai: true, expenses: true };

export interface WaterfallStep {
  key: ProfitComponentKey;
  label: string;
  amount: number;
  missing: boolean;
  incomplete: boolean;
  reason: string | null;
  /** Running total after this step (null once a missing line was subtracted). */
  after: number | null;
}

export interface Combined {
  revenue: number;
  steps: WaterfallStep[];
  /** Σ of the ON lines that are known. */
  costs: number;
  /** Encaissé − ON lines; null when an ON line is missing. */
  result: number | null;
  /** The ON lines that could not be computed. */
  missing: ProfitComponentKey[];
  /** result ÷ encaissé. */
  margin: number | null;
  /** result ÷ delivered orders. */
  perOrder: number | null;
  /** Every line ON — this result is the official Profit net. */
  official: boolean;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Encaissé − the switched-on lines. Pure. */
export function combineProfit(revenue: number, orders: number, comps: ProfitComponents, on: Toggles): Combined {
  let running: number | null = revenue;
  let costs = 0;
  const missing: ProfitComponentKey[] = [];
  const steps: WaterfallStep[] = [];
  for (const line of COST_LINES) {
    if (!on[line.key]) continue;
    const c = comps[line.key];
    if (!c) continue;
    if (c.missing) {
      missing.push(line.key);
      running = null;
    } else {
      costs += c.amount;
      if (running != null) running = r2(running - c.amount);
    }
    steps.push({ key: line.key, label: line.label, amount: c.missing ? 0 : c.amount, missing: c.missing, incomplete: c.incomplete, reason: c.reason, after: running });
  }
  const result = missing.length ? null : r2(revenue - costs);
  return {
    revenue,
    steps,
    costs: r2(costs),
    result,
    missing,
    margin: result != null && revenue > 0 ? result / revenue : null,
    perOrder: result != null && orders > 0 ? r2(result / orders) : null,
    official: COST_LINES.every((l) => on[l.key]),
  };
}

/**
 * The components with the AI line set to the chosen figure. Pure.
 * No payé figure (an older worker) or a missing AI line → unchanged.
 */
export function withAiBasis(comps: ProfitComponents, ai: ProfitSummary['ai'], basis: AiBasis): ProfitComponents {
  if (!ai || !comps.ai || comps.ai.missing) return comps;
  const amount = basis === 'paid' ? ai.paid ?? ai.total : ai.total;
  if (amount === comps.ai.amount) return comps;
  return { ...comps, ai: { ...comps.ai, amount } };
}

/** The worker sent a payé figure that differs from the réel one (some Claude calls were covered). Pure. */
export function aiPaidDiffers(ai: ProfitSummary['ai']): boolean {
  return !!ai && !ai.missing && ai.paid != null && ai.paid !== ai.total;
}

const STORE_KEY = 'storiz.profitCalc.toggles.v1';
const BASIS_KEY = 'storiz.profitCalc.aiBasis.v1';

/** The last Réel/Payé choice (per browser user — convenience only). Default réel. Never throws. */
export function loadAiBasis(userId: string | null | undefined): AiBasis {
  try {
    return window.localStorage.getItem(`${BASIS_KEY}:${userId ?? 'anon'}`) === 'paid' ? 'paid' : 'real';
  } catch {
    return 'real';
  }
}

export function saveAiBasis(userId: string | null | undefined, b: AiBasis): void {
  try { window.localStorage.setItem(`${BASIS_KEY}:${userId ?? 'anon'}`, b); } catch { /* private mode */ }
}

/** The last toggle set (per browser user — convenience only). Never throws. */
export function loadToggles(userId: string | null | undefined): Toggles {
  try {
    const raw = window.localStorage.getItem(`${STORE_KEY}:${userId ?? 'anon'}`);
    if (!raw) return { ...ALL_ON };
    const v = JSON.parse(raw) as Partial<Toggles>;
    const out = { ...ALL_ON };
    for (const l of COST_LINES) if (typeof v[l.key] === 'boolean') out[l.key] = v[l.key] as boolean;
    return out;
  } catch {
    return { ...ALL_ON };
  }
}

export function saveToggles(userId: string | null | undefined, t: Toggles): void {
  try { window.localStorage.setItem(`${STORE_KEY}:${userId ?? 'anon'}`, JSON.stringify(t)); } catch { /* private mode */ }
}

/** Bar geometry of the waterfall: each step a floating bar from `after` to `after + amount`. Pure. */
export function waterfallBars(c: Combined): Array<{ key: string; from: number; to: number; kind: 'revenue' | 'cost' | 'missing' | 'result' }> {
  const bars: Array<{ key: string; from: number; to: number; kind: 'revenue' | 'cost' | 'missing' | 'result' }> = [
    { key: 'revenue', from: 0, to: c.revenue, kind: 'revenue' },
  ];
  let level = c.revenue;
  for (const s of c.steps) {
    if (s.missing) { bars.push({ key: s.key, from: level, to: level, kind: 'missing' }); continue; }
    bars.push({ key: s.key, from: level - s.amount, to: level, kind: 'cost' });
    level -= s.amount;
  }
  bars.push({ key: 'result', from: 0, to: c.result ?? level, kind: 'result' });
  return bars;
}
